import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
    Card, 
    Button, 
    Badge, 
    List, 
    Avatar, 
    Typography, 
    Input, 
    Space, 
    Tag,
    Empty,
    Spin,
    message,
    Drawer
} from 'antd';
import { 
    MessageOutlined, 
    UserOutlined, 
    CloseOutlined, 
    SendOutlined,
    SearchOutlined,
    CalendarOutlined,
    PhoneOutlined,
    MinusOutlined,
    MoreOutlined,
    CheckOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import pusherService from '../../services/pusher';
import notificationService from '../../services/notificationService';
import soundNotificationService from '../../services/soundNotification';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { Search } = Input;

function ChatPopover() {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [leads, setLeads] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const [otherUserTyping, setOtherUserTyping] = useState(false);
    const [typingUserName, setTypingUserName] = useState('');
    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const [loadError, setLoadError] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [showDrawer, setShowDrawer] = useState(false);

    useEffect(() => {
        if (user) {
            loadLeads();
            initializePusher();
            // Request notification permission
            notificationService.requestPermission();
        }
    }, [user]);

    // Mobile detection
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Auto-scroll on messages or lead change
    useEffect(() => {
        if (!selectedLead) return;
        try {
            requestAnimationFrame(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            });
        } catch (_) {}
    }, [selectedLead, messages]);

    useEffect(() => {
        if (selectedLead) {
            fetchMessages();
            markMessagesAsRead();
        }
    }, [selectedLead]);

    // Subscribe to lead channel for read receipts and live updates
    useEffect(() => {
        if (!selectedLead) return;
        let leadChannel = null;
        (async () => {
            try {
                leadChannel = await pusherService.subscribeToLead(selectedLead.id);
                if (leadChannel) {
                    leadChannel.bind('message-read', (data) => {
                        const readIds = new Set(data?.message_ids || []);
                        setMessages((prev) => prev.map(m => readIds.has(m.id) ? { ...m, is_read: true } : m));
                    });
                    // Optional: receive per-lead typing events (if emitted on lead channel)
                    leadChannel.bind('typing', (data) => {
                        if (data.userId !== user.id) {
                            const nameFromEvent = data.userName || getOtherParticipant(selectedLead)?.name || getOtherParticipant(selectedLead)?.business_name || 'Someone';
                            setTypingUserName(nameFromEvent);
                            setOtherUserTyping(true);
                            setTimeout(() => setOtherUserTyping(false), 3000);
                        }
                    });
                    leadChannel.bind('stop-typing', (data) => {
                        if (data.userId !== user.id) {
                            setOtherUserTyping(false);
                            setTypingUserName('');
                        }
                    });
                }
            } catch (e) {
                console.error('Failed to subscribe to lead channel:', e);
            }
        })();
        return () => { try { leadChannel && leadChannel.unbind_all && leadChannel.unbind_all(); } catch(_) {} };
    }, [selectedLead]);

    // Mark as read when window gains focus while chat open on selected lead
    useEffect(() => {
        const onFocus = () => {
            if (isOpen && selectedLead) {
                markMessagesAsRead();
            }
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [isOpen, selectedLead]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const initializePusher = async () => {
        if (!user) return;

        try {
            // Subscribe to user notifications
            const userChannel = await pusherService.subscribeToUser(user.id);
            
            if (user?.user_type === 'vendor' && user.vendor?.id) {
                await pusherService.subscribeToVendor(user.vendor.id);
            }
            
            // Listen for new messages
            if (userChannel) {
                userChannel.bind('new-message', (data) => {
                    if (data.leadId === selectedLead?.id) {
                        setMessages(prev => [...prev, data.message]);
                    }
                    // Update unread count
                    setUnreadCount(prev => prev + 1);
                    // Show notification if chat is not open or minimized
                    if (!isOpen || isMinimized) {
                        const senderName = data.message.user?.name || 'Customer';
                        notificationService.showChatNotification(senderName, data.message.message, data.leadId);
                    }
                    try { 
                        soundNotificationService.playNotificationSound(); 
                        console.log('Playing notification sound for new message');
                    } catch(e) { 
                        console.error('Sound play failed:', e);
                        // Fallback to beep
                        try { soundNotificationService.playBeepSound(); } catch(_) {}
                    }
                });

                // Listen for typing indicators
                userChannel.bind('typing', (data) => {
                    if (data.leadId === selectedLead?.id && data.userId !== user.id) {
                        const nameFromEvent = data.userName || getOtherParticipant(selectedLead)?.name || getOtherParticipant(selectedLead)?.business_name || 'Someone';
                        setTypingUserName(nameFromEvent);
                        setOtherUserTyping(true);
                        setTimeout(() => setOtherUserTyping(false), 3000);
                    }
                });

                userChannel.bind('stop-typing', (data) => {
                    if (data.leadId === selectedLead?.id && data.userId !== user.id) {
                        setOtherUserTyping(false);
                        setTypingUserName('');
                    }
                });
            }

            // Listen for new leads (for vendors)
            if (user?.user_type === 'vendor') {
                const newLeadsChannel = await pusherService.subscribeToChannel('new-leads');
                if (newLeadsChannel) {
                    newLeadsChannel.bind('new-lead', (data) => {
                        // Show notification for new lead
                        notificationService.showLeadNotification(data.lead);
                        // Refresh leads list
                        loadLeads();
                    });
                }
            }

            console.log('Pusher chat popover initialized for user:', user.id);
        } catch (error) {
            console.error('Failed to initialize Pusher chat popover:', error);
        }
    };

    const loadLeads = async () => {
        try {
            setLoading(true);
            setLoadError('');
            const response = user?.user_type === 'vendor' 
                ? await api.getVendorLeads({ per_page: 50 })
                : await api.getMyLeads({ per_page: 50 });
            
            const leadsData = Array.isArray(response?.data?.data)
                ? response.data.data
                : Array.isArray(response?.data) ? response.data : [];
            
            // Sort leads by latest activity (last message or creation time)
            const sortedLeads = leadsData.sort((a, b) => {
                const aTime = new Date(a.last_message_at || a.updated_at || a.created_at);
                const bTime = new Date(b.last_message_at || b.updated_at || b.created_at);
                return bTime - aTime; // Latest first
            });
            setLeads(sortedLeads);
            
            let totalUnread = 0;
            sortedLeads.forEach(lead => {
                if (lead?.messages) {
                    const unreadMessages = lead.messages.filter(msg => 
                        msg.user_id !== user.id && !msg.is_read
                    );
                    totalUnread += unreadMessages.length;
                }
            });
            setUnreadCount(totalUnread);
        } catch (error) {
            console.error('Failed to load leads:', error);
            setLoadError('Failed to load conversations');
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        if (!selectedLead) return;
        
        try {
            const response = await api.getLeadMessages(selectedLead.id);
            if (response.success) {
                setMessages(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch messages:', error);
        }
    };

    const markMessagesAsRead = async () => {
        if (!selectedLead) return;
        
        try {
            await api.markLeadMessagesAsRead(selectedLead.id);
        } catch (error) {
            console.error('Failed to mark messages as read:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || sending || !selectedLead) return;

        try {
            setSending(true);
            const response = await api.sendLeadMessage(selectedLead.id, {
                message: newMessage.trim()
            });

            if (response.success) {
                setMessages(prev => [...prev, response.data]);
                setNewMessage('');
                
                // Stop typing indicator
                pusherService.notifyTyping(selectedLead.id, user.id, false);
            }
        } catch (error) {
            message.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleTyping = (e) => {
        setNewMessage(e.target.value);

        if (selectedLead) {
            pusherService.notifyTyping(selectedLead.id, user.id, true);
            
            // Clear existing timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            
            // Set new timeout to stop typing
            typingTimeoutRef.current = setTimeout(() => {
                pusherService.notifyTyping(selectedLead.id, user.id, false);
            }, 1000);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const getOtherParticipant = (lead) => {
        if (user?.user_type === 'vendor') {
            return lead.user;
        } else {
            return lead.vendor;
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            quoted: 'blue',
            accepted: 'green',
            rejected: 'red',
            completed: 'green'
        };
        return colors[status] || 'default';
    };

    const filteredLeads = (leads || []).filter(lead => {
        const otherParticipant = getOtherParticipant(lead) || {};
        const searchLower = (searchText || '').toLowerCase();
        return (
            (otherParticipant.name || otherParticipant.business_name || '').toLowerCase().includes(searchLower) ||
            (lead.event_type || '').toLowerCase().includes(searchLower) ||
            (lead.venue || '').toLowerCase().includes(searchLower)
        );
    });

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);

        if (diffInHours < 24) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
    };

    const isMyMessage = (message) => {
        return message.user_id === user.id;
    };

    const getMessageStatus = (message) => {
        if (message.is_read) {
            return '✓✓';
        }
        return '✓';
    };

    if (!user) return null;

    // Chat list item component
    const ChatListItem = ({ lead, isSelected, onClick }) => {
        const unreadCount = lead.messages?.filter(msg => 
            msg.user_id !== user.id && !msg.is_read
        ).length || 0;
        
        const lastMessage = lead.messages?.[lead.messages.length - 1];
        const lastMessageTime = lastMessage?.created_at || lead.updated_at || lead.created_at;
        const otherParticipant = getOtherParticipant(lead);
        
        return (
            <div
                className={`flex items-center p-4 cursor-pointer transition-all duration-200 border-b border-gray-100 hover:bg-gray-50 ${
                    isSelected ? 'bg-gray-50' : ''
                }`}
                onClick={onClick}
            >
                <div className="relative">
                    <Avatar
                        size={48}
                        src={otherParticipant?.avatar || otherParticipant?.logo}
                        icon={<UserOutlined />}
                        className="ring-2 ring-gray-100"
                    />
                    {unreadCount > 0 && (
                        <Badge
                            count={unreadCount}
                            size="small"
                            className="absolute -top-1 -right-1"
                            style={{ backgroundColor: '#25D366' }}
                        />
                    )}
                </div>
                <div className="flex-1 ml-3 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                        <Text strong className="text-[#2C2C2C] truncate text-base">
                            {otherParticipant?.name || otherParticipant?.business_name || 'Unknown'}
                        </Text>
                        {lastMessageTime && (
                            <Text className="text-xs text-[#4A4A4A]">
                                {dayjs(lastMessageTime).format('HH:mm')}
                            </Text>
                        )}
                    </div>
                    <div className="flex items-center justify-between">
                        <Text 
                            className={`text-sm truncate ${unreadCount > 0 ? 'font-semibold text-[#2C2C2C]' : 'text-[#4A4A4A]'}`}
                        >
                            {lastMessage?.message || 'No messages yet'}
                        </Text>
                        {lastMessage?.user_id === user.id && (
                            <div className="ml-2">
                                {lastMessage.is_read ? (
                                    <CheckCircleOutlined className="text-green-500 text-xs" />
                                ) : (
                                    <CheckOutlined className="text-gray-400 text-xs" />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Mobile drawer for chat list
    const MobileChatDrawer = () => (
        <Drawer
            title="Messages"
            placement="right"
            onClose={() => setShowDrawer(false)}
            open={showDrawer}
            width={320}
            className="mobile-chat-drawer"
            styles={{
                body: { padding: 0 }
            }}
        >
            <div className="h-full flex flex-col">
                {/* Search */}
                <div className="p-3 border-b bg-gray-50">
                    <Search
                        placeholder="Search conversations..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="rounded-full"
                    />
                </div>
                
                {/* Chat List */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex justify-center items-center h-32">
                            <Spin />
                        </div>
                    ) : leads.length === 0 ? (
                        <Empty
                            description="No conversations yet"
                            className="mt-8"
                        />
                    ) : (
                        leads
                            .filter(lead => 
                                !searchText || 
                                (lead.vendor?.business_name || lead.customer?.name || '').toLowerCase().includes(searchText.toLowerCase())
                            )
                            .map(lead => (
                                <ChatListItem
                                    key={lead.id}
                                    lead={lead}
                                    isSelected={selectedLead?.id === lead.id}
                                    onClick={() => {
                                        setSelectedLead(lead);
                                        setShowDrawer(false);
                                        fetchMessages();
                                    }}
                                />
                            ))
                    )}
                </div>
            </div>
        </Drawer>
    );

    return createPortal(
        <div 
            className="z-50" 
            style={{ 
                position: 'fixed', 
                bottom: '16px', 
                right: '16px',
                zIndex: 2147483647
            }}
        >
            {/* Chat Button */}
            {!isOpen && (
                <Button
                    type="primary"
                    shape="circle"
                    size="large"
                    icon={
                        <Badge count={unreadCount} size="small">
                            <MessageOutlined />
                        </Badge>
                    }
                    onClick={() => setIsOpen(true)}
                    className="shadow-lg hover:shadow-xl transition-all duration-300"
                    style={{ 
                        width: '60px', 
                        height: '60px',
                        background: '#25D366',
                        border: 'none'
                    }}
                />
            )}

            {/* Mobile Chat Interface */}
            {isOpen && isMobile && (
                <div className="fixed inset-0 bg-white z-50 flex flex-col">
                    {/* Mobile Header */}
                    <div className="bg-green-600 text-white p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Button
                                type="text"
                                onClick={() => setIsOpen(false)}
                                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full"
                            >
                                <CloseOutlined />
                            </Button>
                            <MessageOutlined className="text-lg" />
                            <Text strong className="text-white text-lg">
                                Messages
                            </Text>
                            {unreadCount > 0 && (
                                <Badge count={unreadCount} size="small" />
                            )}
                        </div>
                        <Button
                            type="text"
                            onClick={() => setShowDrawer(true)}
                            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full"
                        >
                            <MoreOutlined />
                        </Button>
                    </div>

                    {/* Mobile Content */}
                    <div className="flex-1 flex flex-col min-h-0">
                        {!selectedLead ? (
                            // Mobile Chat List
                            <div className="flex-1 overflow-y-auto min-h-0 bg-gray-50">
                                {loading ? (
                                    <div className="flex justify-center items-center h-32">
                                        <Spin size="small" />
                                    </div>
                                ) : leads.length === 0 ? (
                                    <Empty 
                                        description="No conversations yet"
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        className="mt-8"
                                    />
                                ) : (
                                    <div className="bg-white">
                                        {leads
                                            .filter(lead => 
                                                !searchText || 
                                                (lead.vendor?.business_name || lead.customer?.name || '').toLowerCase().includes(searchText.toLowerCase())
                                            )
                                            .map(lead => (
                                                <ChatListItem
                                                    key={lead.id}
                                                    lead={lead}
                                                    isSelected={selectedLead?.id === lead.id}
                                                    onClick={() => {
                                                        setSelectedLead(lead);
                                                        fetchMessages();
                                                    }}
                                                />
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Mobile Chat Interface (same as desktop but full screen)
                            <div className="flex-1 flex flex-col min-h-0 bg-gray-100">
                                {/* Mobile Chat Header */}
                                <div className="bg-green-600 text-white p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Button
                                            type="text"
                                            onClick={() => setSelectedLead(null)}
                                            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full"
                                        >
                                            ←
                                        </Button>
                                        <Avatar 
                                            size={40} 
                                            icon={<UserOutlined />}
                                            src={getOtherParticipant(selectedLead)?.avatar}
                                            className="ring-2 ring-white"
                                        />
                                        <div>
                                            <Text strong className="text-white text-base">
                                                {getOtherParticipant(selectedLead)?.name || getOtherParticipant(selectedLead)?.business_name}
                                            </Text>
                                            <div className="text-xs text-green-100">
                                                {selectedLead.event_type} • {selectedLead.guest_count} guests
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        type="text"
                                        icon={<MoreOutlined />}
                                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full"
                                    />
                                </div>

                                {/* Mobile Messages - same as desktop */}
                                <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-2 bg-gray-100" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23f0f0f0" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}>
                                    {messages.length === 0 ? (
                                        <Empty 
                                            description="No messages yet" 
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            className="mt-8"
                                        />
                                    ) : (
                                        messages.map((msg, index) => (
                                            <div key={index} className={`flex ${isMyMessage(msg) ? 'justify-end' : 'justify-start'} mb-2`}>
                                                <div className={`max-w-[75%] ${isMyMessage(msg) ? 'order-2' : 'order-1'}`}>
                                                    <div className={`flex items-end gap-2 ${isMyMessage(msg) ? 'flex-row-reverse' : 'flex-row'}`}>
                                                        {!isMyMessage(msg) && (
                                                            <Avatar size="small" icon={<UserOutlined />} className="mb-1" />
                                                        )}
                                                        <div className={`px-4 py-2 rounded-2xl shadow-sm ${
                                                            isMyMessage(msg)
                                                                ? 'bg-green-500 text-white rounded-br-md'
                                                                : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                                                        }`}>
                                                            <div className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</div>
                                                            <div className={`flex items-center gap-1 mt-1 ${isMyMessage(msg) ? 'justify-end' : 'justify-start'}`}>
                                                                <span className={`text-xs ${isMyMessage(msg) ? 'text-green-100' : 'text-gray-500'}`}>
                                                                    {formatTime(msg.created_at)}
                                                                </span>
                                                                {isMyMessage(msg) && (
                                                                    <span className="text-xs text-green-100 ml-1">
                                                                        {getMessageStatus(msg)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Mobile Typing Indicator */}
                                {otherUserTyping && (
                                    <div className="px-4 py-2 text-sm text-gray-500 italic flex items-center gap-2 bg-white border-t">
                                        <div className="flex space-x-1">
                                            <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
                                            <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                            <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                        </div>
                                        {typingUserName || 'Someone'} is typing…
                                    </div>
                                )}

                                {/* Mobile Message Input */}
                                <div className="p-3 bg-white border-t">
                                    <div className="flex gap-2 items-end">
                                        <Input
                                            value={newMessage}
                                            onChange={handleTyping}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Type a message..."
                                            size="large"
                                            disabled={sending}
                                            className="flex-1 rounded-full border-gray-300 focus:border-green-500 focus:ring-green-500"
                                            style={{ borderRadius: '25px' }}
                                        />
                                        <Button
                                            type="primary"
                                            icon={<SendOutlined />}
                                            onClick={handleSendMessage}
                                            loading={sending}
                                            disabled={!newMessage.trim()}
                                            size="large"
                                            className="bg-green-500 border-green-500 hover:bg-green-600 hover:border-green-600 rounded-full"
                                            style={{ borderRadius: '50%', width: '40px', height: '40px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Mobile Drawer */}
            {isMobile && <MobileChatDrawer />}

            {/* Desktop Chat Popover */}
            {isOpen && !isMobile && (
                <div className="fixed bottom-24 right-6 w-[380px] max-w-[90vw] bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
                    {loadError && (
                        <div className="bg-red-50 text-red-700 text-sm px-3 py-2 flex items-center justify-between">
                            <span>{loadError}</span>
                            <Button size="small" type="link" onClick={loadLeads}>Retry</Button>
                        </div>
                    )}
                    <div
                        className={`transition-all duration-300 overflow-hidden rounded-lg bg-white ${
                            isMinimized ? 'h-16' : 'h-[600px]'
                        }`}
                        style={{ 
                            width: '380px',
                            maxHeight: '75vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {isMinimized ? (
                            // Minimized Header
                            <div className="flex items-center justify-between p-4 bg-green-600">
                                <div className="flex items-center gap-2">
                                    <MessageOutlined className="text-white" />
                                    <Text strong className="text-white">Messages</Text>
                                    {unreadCount > 0 && (
                                        <Badge count={unreadCount} size="small" />
                                    )}
                                </div>
                                <Space>
                                    <Button
                                        type="text"
                                        icon={<MessageOutlined />}
                                        onClick={() => setIsMinimized(false)}
                                        size="small"
                                        className="text-white hover:bg-white hover:bg-opacity-20"
                                    />
                                    <Button
                                        type="text"
                                        icon={<CloseOutlined />}
                                        onClick={() => setIsOpen(false)}
                                        size="small"
                                        className="text-white hover:bg-white hover:bg-opacity-20"
                                    />
                                </Space>
                            </div>
                        ) : (
                            // Full Chat Interface
                            <>
                                {/* Green Header */}
                                <div className="bg-green-600 text-white p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <Text strong className="text-white text-lg">
                                            Messages
                                        </Text>
                                        <Space>
                                            <Button
                                                type="text"
                                                icon={<MinusOutlined />}
                                                onClick={() => setIsMinimized(true)}
                                                className="text-white hover:bg-white hover:bg-opacity-20"
                                                size="small"
                                            />
                                            <Button
                                                type="text"
                                                icon={<CloseOutlined />}
                                                onClick={() => setIsOpen(false)}
                                                className="text-white hover:bg-white hover:bg-opacity-20"
                                                size="small"
                                            />
                                        </Space>
                                    </div>
                                    
                                    {!selectedLead && (
                                        <Input
                                            placeholder="Search conversations..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            prefix={<SearchOutlined className="text-gray-400" />}
                                            allowClear
                                            className="bg-white rounded-lg"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    )}
                                </div>

                                {/* Content */}
                                <div className="flex-1 flex min-h-0 bg-white">
                                    {!selectedLead ? (
                                        // Chat List
                                        <div className="flex-1 overflow-y-auto min-h-0">
                                            {loading ? (
                                                <div className="flex justify-center items-center h-32">
                                                    <Spin size="small" />
                                                </div>
                                            ) : filteredLeads.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center h-full py-12">
                                                    <div className="w-16 h-16 mb-4 flex items-center justify-center">
                                                        <MessageOutlined className="text-6xl text-gray-300" />
                                                    </div>
                                                    <Text type="secondary" className="text-base">
                                                        No conversations yet
                                                    </Text>
                                                </div>
                                            ) : (
                                                <div>
                                                    {filteredLeads.map(lead => (
                                                        <ChatListItem
                                                            key={lead.id}
                                                            lead={lead}
                                                            isSelected={false}
                                                            onClick={() => {
                                                                setSelectedLead(lead);
                                                                fetchMessages();
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        // WhatsApp-like Chat Interface
                                        <div className="flex-1 flex flex-col min-h-0 bg-gray-100">
                                            {/* Chat Header */}
                                            <div className="bg-green-600 text-white p-3 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Button
                                                        type="text"
                                                        onClick={() => setSelectedLead(null)}
                                                        size="small"
                                                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full"
                                                    >
                                                        ←
                                                    </Button>
                                                    <Avatar 
                                                        size={40} 
                                                        icon={<UserOutlined />}
                                                        src={getOtherParticipant(selectedLead)?.avatar}
                                                        className="ring-2 ring-white"
                                                    />
                                                    <div>
                                                        <Text strong className="text-white text-base">
                                                            {getOtherParticipant(selectedLead)?.name || getOtherParticipant(selectedLead)?.business_name}
                                                        </Text>
                                                        <div className="text-xs text-green-100">
                                                            {selectedLead.event_type} • {selectedLead.guest_count} guests
                                                        </div>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="text"
                                                    icon={<MoreOutlined />}
                                                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full"
                                                />
                                            </div>

                                            {/* Messages */}
                                            <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-2 bg-gray-100" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23f0f0f0" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}>
                                                {messages.length === 0 ? (
                                                    <Empty 
                                                        description="No messages yet" 
                                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                        className="mt-8"
                                                    />
                                                ) : (
                                                    messages.map((msg, index) => (
                                                        <div key={index} className={`flex ${isMyMessage(msg) ? 'justify-end' : 'justify-start'} mb-2`}>
                                                            <div className={`max-w-[75%] ${isMyMessage(msg) ? 'order-2' : 'order-1'}`}>
                                                                <div className={`flex items-end gap-2 ${isMyMessage(msg) ? 'flex-row-reverse' : 'flex-row'}`}>
                                                                    {!isMyMessage(msg) && (
                                                                        <Avatar size="small" icon={<UserOutlined />} className="mb-1" />
                                                                    )}
                                                                    <div className={`px-4 py-2 rounded-2xl shadow-sm ${
                                                                        isMyMessage(msg)
                                                                            ? 'bg-green-500 text-white rounded-br-md' // WhatsApp-like green bubble
                                                                            : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                                                                    }`}>
                                                                        <div className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</div>
                                                                        <div className={`flex items-center gap-1 mt-1 ${isMyMessage(msg) ? 'justify-end' : 'justify-start'}`}>
                                                                            <span className={`text-xs ${isMyMessage(msg) ? 'text-green-100' : 'text-gray-500'}`}>
                                                                                {formatTime(msg.created_at)}
                                                                            </span>
                                                                            {isMyMessage(msg) && (
                                                                                <span className="text-xs text-green-100 ml-1">
                                                                                    {getMessageStatus(msg)}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                                <div ref={messagesEndRef} />
                                            </div>

                                            {/* Typing Indicator */}
                                            {otherUserTyping && (
                                                <div className="px-4 py-2 text-sm text-gray-500 italic flex items-center gap-2 bg-white border-t">
                                                    <div className="flex space-x-1">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
                                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                                    </div>
                                                    {typingUserName || 'Someone'} is typing…
                                                </div>
                                            )}

                                            {/* Message Input */}
                                            <div className="p-3 bg-white border-t">
                                                <div className="flex gap-2 items-end">
                                                    <Input
                                                        value={newMessage}
                                                        onChange={handleTyping}
                                                        onKeyPress={handleKeyPress}
                                                        placeholder="Type a message..."
                                                        size="large"
                                                        disabled={sending}
                                                        className="flex-1 rounded-full border-gray-300 focus:border-green-500 focus:ring-green-500"
                                                        style={{ borderRadius: '25px' }}
                                                    />
                                                    <Button
                                                        type="primary"
                                                        icon={<SendOutlined />}
                                                        onClick={handleSendMessage}
                                                        loading={sending}
                                                        disabled={!newMessage.trim()}
                                                        size="large"
                                                        className="bg-green-500 border-green-500 hover:bg-green-600 hover:border-green-600 rounded-full"
                                                        style={{ borderRadius: '50%', width: '40px', height: '40px' }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
}

export default ChatPopover;
